import PropTypes from 'prop-types';
import React, {
  useContext, useEffect, useState,
} from 'react';

import { ColorProfiles, ScreenType } from '@powdr/constants';
import { AppContext } from '@powdr/context';
import { componentFactory } from '@powdr/utils';

import { ActiveTab } from './components';
import {
  StyledComponentTabs, TabsContainer,
  IndividualTabWrapper, IndividualTab,
  ActiveTabTopAccent, ActiveTabConnector,
  TabButton, TabButtonIcon, TabTitle,
  AccordionIndicator,
} from './styles';

export const ComponentTabs = ({
  className = '',
  parentColorProfile,
  relationships,
}) => {
  const { isMobile, themeConfig } = useContext(AppContext);
  const { tabs } = relationships;
  const [activeTab, setActiveTab] = useState();
  const alternateMobileInidicatorIcon = themeConfig?.layout?.componentTabsAltIcon || null;

  useEffect(() => {
    if (!isMobile) {
      setActiveTab(0);
    } else {
      setActiveTab(null);
    }
  }, [isMobile]);

  const handleTabClick = (index) => {
    if (index === activeTab && isMobile) {
      setActiveTab(null);
    } else {
      setActiveTab(index);
    }
  };

  return (
    <StyledComponentTabs className={className}>
      <TabsContainer>
        {tabs.map((tab, index) => {
          const {
            id, tabTitle, tabTitleIcon,
            containerTitle, tabContainerIcon, containerContent,
            tabColorProfile: tcp, containerColorProfileOverride, isWoodwardColorOverride,
            relationships: { component, buttons },
          } = tab;

          const tabColorProfile = tcp || ColorProfiles.PRIMARY;

          return (
            <IndividualTabWrapper
              key={id}
              $isWoodwardColorOverride={isWoodwardColorOverride}
            >
              <IndividualTab
                index={index}
                numTabs={tabs.length}
                tabIndex={(index === activeTab) ? -1 : 0}
                $colorProfile={tabColorProfile}
              >
                <TabButton
                  onClick={() => handleTabClick(index)}
                  onMouseDown={(e) => e.preventDefault()}
                  $activeTab={index === activeTab}
                  $colorProfile={tabColorProfile}
                >
                  <TabButtonIcon $colorProfile={tabColorProfile} name={tabTitleIcon} width="25" />
                  <TabTitle $colorProfile={tabColorProfile}>{tabTitle}</TabTitle>
                  {(isMobile) && (
                    <AccordionIndicator
                      $colorProfile={tabColorProfile}
                      $activeTab={index === activeTab}
                      name={alternateMobileInidicatorIcon || 'ui-arrow-down'}
                      width="25"
                    />
                  )}
                </TabButton>
                {(index === activeTab) && (
                  <>
                    <ActiveTabTopAccent
                      $colorProfile={tabColorProfile}
                    />
                    <ActiveTabConnector
                      $colorProfile={containerColorProfileOverride || tabColorProfile}
                      $adjustHeight={!!(containerColorProfileOverride
                        && (containerColorProfileOverride === parentColorProfile)
                      )}
                    />
                  </>
                )}
                {(isMobile && activeTab === index) && (
                  <ActiveTab
                    platform={ScreenType.MOBILE}
                    colorProfile={containerColorProfileOverride || tabColorProfile}
                    title={containerTitle || undefined}
                    icon={tabContainerIcon || undefined}
                    content={containerContent?.value || undefined}
                    buttons={buttons || undefined}
                  >
                    {componentFactory(
                      [component],
                      containerColorProfileOverride || tabColorProfile || ColorProfiles.PRIMARY,
                      undefined,
                      {
                        title: tabTitle,
                        colorProfile: containerColorProfileOverride
                          || tabColorProfile
                          || ColorProfiles.PRIMARY,
                      },
                    )}
                  </ActiveTab>
                )}
              </IndividualTab>
            </IndividualTabWrapper>
          );
        })}
      </TabsContainer>
      {(!isMobile) && (
        <ActiveTab
          colorProfile={tabs?.[activeTab]?.containerColorProfileOverride
            || tabs?.[activeTab]?.tabColorProfile
            || ColorProfiles.PRIMARY}
          platform={ScreenType.DESKTOP}
          title={tabs?.[activeTab]?.containerTitle || undefined}
          icon={tabs?.[activeTab]?.tabContainerIcon || undefined}
          content={tabs?.[activeTab]?.containerContent?.value || undefined}
          buttons={tabs?.[activeTab]?.relationships?.buttons || undefined}
        >
          {componentFactory(
            [tabs?.[activeTab]?.relationships?.component],
            tabs?.[activeTab]?.containerColorProfileOverride
              || tabs?.[activeTab]?.tabColorProfile
              || ColorProfiles.PRIMARY,
            undefined,
            {
              title: tabs?.[activeTab]?.tabTitle,
              colorProfile: tabs?.[activeTab]?.containerColorProfileOverride
                || tabs?.[activeTab]?.tabColorProfile
                || ColorProfiles.PRIMARY,
            },
          )}
        </ActiveTab>
      )}
    </StyledComponentTabs>
  );
};

ComponentTabs.propTypes = {
  className: PropTypes.string,
  parentColorProfile: PropTypes.string,
  relationships: PropTypes.shape({
    tabs: PropTypes.arrayOf(PropTypes.shape({
      tabTitle: PropTypes.string,
      tabColorProfile: PropTypes.string,
      tabContainerIcon: PropTypes.string,
      containerColorProfileOverride: PropTypes.string,
      containerTitle: PropTypes.string,
      containerContent: PropTypes.shape({
        value: PropTypes.string,
      }),
      relationships: PropTypes.shape({
        component: PropTypes.shape(),
        buttons: PropTypes.shape(),
      }),
    })),
  }).isRequired,
};
